import { FormNotice, theme } from '@zappyrent/hundop';
import { AxiosError, AxiosResponse } from 'axios';
import { isEmpty, upperFirst } from 'lodash';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Media from 'react-media';
import { useMutation, useQueries, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import TelephoneInput from '../../components/___design/TelephoneInput';
import TextInput from '../../components/___design/TextInput';
import NotFound from '../../components/illustrations/NotFound';
import NavBar from '../../components/NavBar';
import { H2Text, H3Text } from '../../components/UI/Text';
import { ValidationRules } from '../../components/UploadDocuments/AddALandlordReferral/AddALandlordReferral.types';
import { TenantLandlordReferralWebpageSource } from '../../components/UploadDocuments/AddALandlordReferral/constant/tenant-landlord-referral-field.contant';
import { useToastContext } from '../../contexts/ToastProvider';
import tenantLandlordReferralApi from '../../services/api/tenant-landlord-referral';
import visitsAPI from '../../services/api/visits';
import { redirectExploreProperties } from '../../shared/helpers/redirect';
import { ILandlortReferral } from '../../shared/ts/landlord-referrals';
import { VisitsQuestionnaireDto } from '../../types/visits';
import {
  ButtonContainer,
  IllustrationContainer,
  StyledBodyContainer,
  StyledButton,
  StyledContainer,
  StyledContinueButton,
  StyledSpinner,
} from './VisitRequestWasSent.style';

const VisitRequestWasSent = (): JSX.Element | null => {
  const { t } = useTranslation([
    'requestwassent',
    'errors',
    'common',
    'form',
    'formError',
  ]);
  const [visit, setVisit] = useState<VisitsQuestionnaireDto | null>(null);
  const { visitId, locale } = useParams();
  const toast = useToastContext();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [landlordReferral, setLandlordReferral] = useState<ILandlortReferral>(
    {} as ILandlortReferral,
  );
  const { mutateAsync: mutateVisitLandlordReferralsCreateAsync } = useMutation(
    tenantLandlordReferralApi.createLandlordReferralsForVisit,
  );
  const onClickExploreProperties = () => {
    visitId && locale && redirectExploreProperties(visitId, locale);
  };

  const { control, setError, handleSubmit, reset } = useForm({
    defaultValues: {
      lead_name: '',
      lead_phone_number: '',
      time_to_contact: '',
    },
  });

  useQuery({
    queryKey: ['visit'],
    queryFn: () => visitsAPI.getVisitsQuestionnaire(visitId as string),

    onSuccess(response) {
      setVisit(response);
    },
    onError(error: AxiosError) {
      console.error(error);
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const [
    { data: questionaire_visit_data, isLoading: questionaire_visit_loading },
    { data: visit_agenda_data, isLoading: visit_agenda_loading },
  ] = useQueries([
    {
      queryKey: [
        'landlord-referral',
        visitId,
        TenantLandlordReferralWebpageSource.QUESTIONNAIRE_VISIT,
      ],
      queryFn: () =>
        tenantLandlordReferralApi.getLandlordReferrals({
          visitId: visitId,
          webpage_source:
            TenantLandlordReferralWebpageSource.QUESTIONNAIRE_VISIT,
        }),
    },
    {
      queryKey: [
        'landlord-referral',
        visitId,
        TenantLandlordReferralWebpageSource.VISIT_AGENDA,
      ],
      queryFn: () =>
        tenantLandlordReferralApi.getLandlordReferrals({
          visitId: visitId,
          webpage_source: TenantLandlordReferralWebpageSource.VISIT_AGENDA,
        }),
    },
  ]);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'landlord-referral',
      visitId,
      TenantLandlordReferralWebpageSource.VISIT_ON_HOLD,
    ],
    queryFn: () =>
      tenantLandlordReferralApi.getLandlordReferrals({
        visitId: visitId,
        webpage_source: TenantLandlordReferralWebpageSource.VISIT_ON_HOLD,
      }),
    onSuccess: (response: AxiosResponse<Array<ILandlortReferral>>) => {
      setLandlordReferral(response.data[0] ?? {});
    },
    onError(error: AxiosError) {
      console.error(error);
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const validationRules: ValidationRules = {
    similar_lead_phone_number: (value: string) => {
      return value === visit?.phone_no?.toString()?.split(' ')?.join('');
    },
  };

  const handleFormSubmit = async (
    values: Pick<
      ILandlortReferral,
      'lead_name' | 'lead_phone_number' | 'time_to_contact'
    >,
  ) => {
    try {
      if (values.lead_name && !values.lead_phone_number) {
        setError('lead_phone_number', {
          message: upperFirst(t('form:requiredField')),
        });
        setIsSubmitLoading(false);
        return;
      }

      if (values.lead_phone_number && !values.lead_name) {
        setError('lead_name', {
          message: upperFirst(t('form:requiredField')),
        });
        setIsSubmitLoading(false);
        return;
      }

      if (
        validationRules?.similar_lead_phone_number(values?.lead_phone_number)
      ) {
        setError('lead_phone_number', {
          message: upperFirst(t('formError:sameField')),
        });
        setIsSubmitLoading(false);
        return;
      }

      if (values.lead_name && values.lead_phone_number) {
        await mutateVisitLandlordReferralsCreateAsync({
          visitId: visitId!,
          data: values,
          webpageSource: TenantLandlordReferralWebpageSource.VISIT_ON_HOLD,
        });
        refetch();
      }

      toast.success(
        upperFirst(t('requestwassent:addANewReferralSubmittedSuccessFully')),
      );
    } catch (error) {
      toast.error(upperFirst(t('errors:error')));
    } finally {
      setIsSubmitLoading(false);
    }
  };

  useEffect(() => {
    if (landlordReferral) {
      reset({
        lead_name: landlordReferral.lead_name,
        lead_phone_number: landlordReferral.lead_phone_number,
        time_to_contact: landlordReferral.time_to_contact,
      });
    }
  }, [landlordReferral]);

  return (
    <StyledContainer>
      <NavBar />
      <StyledBodyContainer>
        <H2Text
          alignSelf="center"
          padding="0"
          smPadding="0 5rem"
          mdPadding="0 13rem"
          lgPadding="0 21.5rem"
        >
          {t('requestwassent:firstHeader')}
        </H2Text>
        <H3Text
          fontWeight={'lighter'}
          textAlign="center"
          padding="0"
          smPadding="0 5rem"
          mdPadding="0 14rem"
          lgPadding="0 21rem"
        >
          {t('requestwassent:secondHeader')}
        </H3Text>
        <IllustrationContainer>
          <NotFound />
        </IllustrationContainer>
        {!(
          !isEmpty(questionaire_visit_data?.data) ||
          !isEmpty(visit_agenda_data?.data)
        ) &&
          !questionaire_visit_loading &&
          !visit_agenda_loading &&
          !isLoading && (
            <Media
              queries={{
                mobile: `(max-width: calc(${theme.breakpoints.m}))`,
                desktop: `(min-width: ${theme.breakpoints.m})`,
              }}
            >
              {matches => (
                <form
                  id="visit-questionnaire-referral-form"
                  noValidate
                  style={{
                    width: matches.mobile ? '100%' : '50%',
                    margin: '0 auto',
                  }}
                  onSubmit={handleSubmit(handleFormSubmit)}
                >
                  <Controller
                    control={control}
                    name="lead_name"
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <TextInput
                        id="lead_name"
                        label={upperFirst(t(`form:lead_name`))}
                        name="lead_name"
                        type="text"
                        value={value}
                        onChange={value => {
                          setError('lead_name', { message: '' });
                          onChange(value);
                        }}
                      >
                        {error?.message && (
                          <FormNotice isWrapped={true}>
                            {error?.message}
                          </FormNotice>
                        )}
                      </TextInput>
                    )}
                  />
                  <Controller
                    control={control}
                    name="lead_phone_number"
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <TelephoneInput
                        id="lead_phone_number"
                        label={upperFirst(t(`form:lead_phone_number`))}
                        name="lead_phone_number"
                        value={value}
                        onChange={value => {
                          setError('lead_phone_number', { message: '' });
                          onChange(value.split(' ').join(''));
                        }}
                      >
                        {error?.message && (
                          <FormNotice isWrapped={true}>
                            {error?.message}
                          </FormNotice>
                        )}
                      </TelephoneInput>
                    )}
                  />
                  <Controller
                    control={control}
                    name="time_to_contact"
                    render={({ field: { onChange, value } }) => (
                      <TextInput
                        id="time_to_contact"
                        label={upperFirst(t(`form:time_to_contact`))}
                        name="time_to_contact"
                        type="text"
                        value={value}
                        onChange={value => onChange(value)}
                      ></TextInput>
                    )}
                  />
                  {!landlordReferral.id && (
                    <ButtonContainer>
                      <StyledContinueButton title={t('common:confirm')}>
                        {isSubmitLoading && (
                          <StyledSpinner
                            foregroundColor={theme.colors.cross.ultralightGray}
                            size={15}
                            strokeSize={2}
                          />
                        )}
                        {t('common:confirm')}
                      </StyledContinueButton>
                    </ButtonContainer>
                  )}
                </form>
              )}
            </Media>
          )}
        <StyledButton onClick={onClickExploreProperties}>
          {t('requestwassent:exploreProperties')}
        </StyledButton>
      </StyledBodyContainer>
    </StyledContainer>
  );
};
export default VisitRequestWasSent;
